/**
 * Q-e: History page — Awaiting Purge / Purged tabs.
 *
 * Mirrors RetentionQueuePage layout and Industrial Core conventions
 * (conductor/DESIGN.md): uppercase headers, hairline borders, instant hover,
 * Safety Orange focus ring, plate-on-plate surface layering.
 *
 * Task 9: Purge now button on awaiting-purge rows → ConfirmModal → purgeNow
 * client → refresh. Mirrors RetentionQueuePage's purge UX exactly.
 *
 * Task 13: Reprocess button on awaiting-purge rows → target-step picker modal
 * → reprocessDoc client → refresh. Purged rows show disabled Reprocess with
 * "Images purged — reprocessing unavailable" reason.
 */
import { useState } from "react";
import { useHistory } from "../hooks/useHistory";
import { purgeNow, reprocessDoc, type ReprocessTargetStep } from "../api/history-client";
import type { HistoryFilter, AwaitingPurgeItem, PurgedItem, HistoryItem } from "../api/history-client";
import { ConfirmModal } from "@/components/ConfirmModal";
import { toast } from "@/components/Toaster";
import styles from "./HistoryPage.module.css";
import { formatMinutesRemaining } from "@/lib/formatters";

function isAwaitingPurge(item: HistoryItem): item is AwaitingPurgeItem {
  return "minutesRemaining" in item;
}

// ── Reprocess picker modal ────────────────────────────────────────────────────

const REPROCESS_STEPS: { step: ReprocessTargetStep; label: string }[] = [
  { step: "scan",   label: "Re-scan (re-OCR)" },
  { step: "key",    label: "Back to Key" },
  { step: "verify", label: "Back to Verify" },
  { step: "qc",     label: "Back to QC" },
  { step: "export", label: "Re-export" },
];

interface ReprocessPickerModalProps {
  open: boolean;
  displayId: string;
  onPick: (step: ReprocessTargetStep) => void;
  onCancel: () => void;
}

function ReprocessPickerModal({ open, displayId, onPick, onCancel }: ReprocessPickerModalProps) {
  if (!open) return null;

  return (
    <div className="text-prompt-overlay" onClick={onCancel}>
      <div
        className="text-prompt-panel"
        role="alertdialog"
        aria-labelledby="reprocess-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="reprocess-picker-title" className="text-prompt-title">
          Reprocess — {displayId}
        </h2>
        <p className="text-prompt-description">
          Choose the pipeline step to send this document back to.
        </p>
        <div className="text-prompt-actions" style={{ flexWrap: "wrap", gap: "var(--space-2)" }}>
          <button type="button" className="text-prompt-btn" onClick={onCancel}>
            Cancel
          </button>
          {REPROCESS_STEPS.map(({ step, label }) => (
            <button
              key={step}
              type="button"
              className="text-prompt-btn text-prompt-btn--primary"
              onClick={() => onPick(step)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Awaiting-purge row ────────────────────────────────────────────────────────

interface AwaitingPurgeRowProps {
  item: AwaitingPurgeItem;
  onPurge: (item: AwaitingPurgeItem) => void;
  onReprocess: (item: AwaitingPurgeItem) => void;
}

function AwaitingPurgeRow({ item, onPurge, onReprocess }: AwaitingPurgeRowProps) {
  const eligible = item.minutesRemaining <= 0;
  const timeClass = eligible
    ? styles.timeUrgent
    : item.minutesRemaining <= 60
      ? styles.timeWarning
      : styles.timeRemaining;

  return (
    <tr className={eligible ? styles.eligibleRow : undefined}>
      <td>
        <span className={styles.displayId}>{item.displayId}</span>
      </td>
      <td>
        <span className={styles.jobType}>{item.jobTypeName ?? "—"}</span>
      </td>
      <td>
        <span className={timeClass}>{formatMinutesRemaining(item.minutesRemaining)}</span>
        {eligible && (
          <span className={styles.eligibleBadge} aria-label="Purge eligible">
            Purge eligible
          </span>
        )}
      </td>
      <td>
        <span className={styles.filename}>{item.filename}</span>
      </td>
      <td>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btn}
            aria-label={`Reprocess ${item.displayId}`}
            onClick={() => onReprocess(item)}
          >
            Reprocess
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`}
            aria-label={`Purge now ${item.displayId}`}
            onClick={() => onPurge(item)}
          >
            Purge now
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Purged row ────────────────────────────────────────────────────────────────

function PurgedRow({ item }: { item: PurgedItem }) {
  const purgedDate = new Date(item.imagesPurgedAt).toLocaleDateString();
  return (
    <tr>
      <td>
        <span className={styles.displayId}>{item.displayId}</span>
      </td>
      <td>
        <span className={styles.jobType}>{item.jobTypeName ?? "—"}</span>
      </td>
      <td>
        <span className={styles.purgedAt}>{purgedDate}</span>
      </td>
      <td>
        <span className={styles.filename}>{item.filename}</span>
      </td>
      <td>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btn}
            disabled
            aria-label="Reprocess"
            title="Images purged — reprocessing unavailable"
          >
            Reprocess
          </button>
          <span className={styles.disabledReason}>Images purged — reprocessing unavailable</span>
        </div>
      </td>
    </tr>
  );
}

// ── Tab content ───────────────────────────────────────────────────────────────

function AwaitingPurgeTab() {
  const { items, isLoading, error, refresh } = useHistory({ filter: "awaiting_purge" });
  const awaitingItems = items.filter(isAwaitingPurge);

  const [purgeTarget, setPurgeTarget] = useState<AwaitingPurgeItem | null>(null);
  const [reprocessTarget, setReprocessTarget] = useState<AwaitingPurgeItem | null>(null);

  async function handlePurgeConfirm() {
    if (!purgeTarget) return;
    const target = purgeTarget;
    setPurgeTarget(null);
    try {
      await purgeNow(target.id);
      toast.success(`Images deleted for ${target.displayId}.`);
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Purge failed.");
    }
  }

  async function handleReprocessPick(step: ReprocessTargetStep) {
    if (!reprocessTarget) return;
    const target = reprocessTarget;
    setReprocessTarget(null);
    try {
      await reprocessDoc(target.id, step);
      toast.success(`${target.displayId} sent back to ${step}.`);
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Reprocess failed.");
    }
  }

  if (error) return <div className={styles.errorBanner}>{error}</div>;
  if (isLoading) return <p className={styles.loading}>Loading…</p>;
  if (awaitingItems.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateTitle}>No documents awaiting purge</p>
        <p className={styles.emptyStateBody}>
          Documents in a post-export retention window appear here until their images are
          purged.
        </p>
      </div>
    );
  }

  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">Document ID</th>
            <th scope="col">Job Type</th>
            <th scope="col">Time Remaining</th>
            <th scope="col">Filename</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {awaitingItems.map((item) => (
            <AwaitingPurgeRow
              key={item.id}
              item={item}
              onPurge={setPurgeTarget}
              onReprocess={setReprocessTarget}
            />
          ))}
        </tbody>
      </table>

      {/* Modals rendered outside <table> to avoid invalid DOM nesting */}
      <ConfirmModal
        open={purgeTarget !== null}
        title={`Delete images — ${purgeTarget?.displayId ?? ""}?`}
        description="Delete the images for this document? The record is kept; images cannot be recovered."
        confirmLabel="Delete images"
        cancelLabel="Cancel"
        destructive
        onConfirm={() => void handlePurgeConfirm()}
        onCancel={() => setPurgeTarget(null)}
      />

      <ReprocessPickerModal
        open={reprocessTarget !== null}
        displayId={reprocessTarget?.displayId ?? ""}
        onPick={(step) => void handleReprocessPick(step)}
        onCancel={() => setReprocessTarget(null)}
      />
    </>
  );
}

function PurgedTab() {
  const { items, isLoading, error } = useHistory({ filter: "purged" });
  const purgedItems = items.filter((i): i is PurgedItem => !isAwaitingPurge(i));

  if (error) return <div className={styles.errorBanner}>{error}</div>;
  if (isLoading) return <p className={styles.loading}>Loading…</p>;
  if (purgedItems.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateTitle}>No purged documents</p>
        <p className={styles.emptyStateBody}>
          Documents whose images have been permanently removed appear here.
        </p>
      </div>
    );
  }

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th scope="col">Document ID</th>
          <th scope="col">Job Type</th>
          <th scope="col">Purged At</th>
          <th scope="col">Filename</th>
          <th scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {purgedItems.map((item) => (
          <PurgedRow key={item.id} item={item} />
        ))}
      </tbody>
    </table>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const TABS: { id: HistoryFilter; label: string }[] = [
  { id: "awaiting_purge", label: "Awaiting purge" },
  { id: "purged", label: "Purged" },
];

export function HistoryPage() {
  const [activeTab, setActiveTab] = useState<HistoryFilter>("awaiting_purge");

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>History</h1>
      <p className={styles.subhead}>
        Documents in their post-export retention window, and those whose images have
        already been purged.
      </p>

      {/* Tab bar */}
      <div className={styles.tabs} role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={
              activeTab === tab.id
                ? `${styles.tab} ${styles.tabActive}`
                : styles.tab
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab panel */}
      <div role="tabpanel">
        {activeTab === "awaiting_purge" ? <AwaitingPurgeTab /> : <PurgedTab />}
      </div>
    </div>
  );
}
